import { useWizardSection } from '@components/Wizard';
import { useUser } from '@hooks/user';
import { useFlags } from '@hooks/flags';
import { Status } from '@constants/status';
import { useSnackbar } from 'notistack';
import { useGeneralNavigation } from '../../hooks/useGeneralNavigation';
import { handleErrors } from '../../utils';
import { useDialogContext } from '@providers/DialogContext';
import { adServerOutOfSyncPrompt } from '@v2/components/ui/AdServerOutOfSyncPrompt';
import { targetingOverrideParams } from '../constants';
import { wizardGeneralErrorFields } from '../../constants';
import { fields } from '@components/CampaignWizard/AdGroupSettingsSection/formConfig';
import { fields as creativeFields } from '@v2/components/campaign/CampaignAdGroupSection/AdvancedSubflows/AdvancedCreative/Manage/constants';
import {
  getOutOfSyncItems,
  showOutOfSyncSubmitPrompt,
} from '../utils/general';
import type { Campaign } from '@local-types/campaign';
import type { UseFormReturn } from 'react-hook-form';
import type { Fields } from '@local-types/general';
import { AdGroup } from '@local-types/group';
import { useGroupSectionSubmitStaticAdGroup } from './useGroupSectionSubmitStaticAdGroup';
import { useGroupSectionSubmitAdGroup } from './useGroupSectionSubmitAdGroup';

export const useGroupSectionSubmit = ({
  isDisplay,
  campaign,
  form,
  sectionKey,
  adGroup,
}: {
  isDisplay: boolean;
  campaign: Campaign;
  form: UseFormReturn<Fields<typeof fields>>;
  sectionKey: string;
  adGroup: AdGroup;
}) => {
  const { user } = useUser();
  const { flags, Flags } = useFlags();
  const { setDialog } = useDialogContext();
  const { enqueueSnackbar } = useSnackbar();
  const { state, toCampaignDetails } = useGeneralNavigation();

  const isTargetingOverrideEnabled =
    flags[Flags.TARGETING_OVERRIDE_THRU_QUERY_PARAM_FEATURE];

  const shouldShowOutOfSyncSubmitPrompt = showOutOfSyncSubmitPrompt({
    adGroup,
    user,
    isTargetingOverrideEnabled,
  });

  const { goToNext } = useWizardSection({
    key: sectionKey,
  });

  const handleAfterSubmit = () => {
    if (state.singleEdit && campaign.status === Status.ACTIVE) {
      toCampaignDetails({ campaign });
    } else {
      goToNext({ checkDirty: false });
    }
  };

  const { submit: staticAdGroupSubmit, mutating: staticAdGroupMutating } =
    useGroupSectionSubmitStaticAdGroup({
      form,
      onAfterSubmit: handleAfterSubmit,
      adGroup,
      shouldShowOutOfSyncSubmitPrompt,
      disabled: !isDisplay,
      campaign,
    });

  const { submit: adGroupSubmit, mutating: adGroupMutating } =
    useGroupSectionSubmitAdGroup({
      form,
      onAfterSubmit: handleAfterSubmit,
      adGroup,
      shouldShowOutOfSyncSubmitPrompt,
      disabled: !!isDisplay,
      campaign,
    });

  const handleGeneralError = (message: string) => {
    enqueueSnackbar(message, { variant: 'error' });
  };

  const submit = handleErrors(
    isDisplay ? staticAdGroupSubmit : adGroupSubmit,
    {
      setError: form.setError,
      generalError: handleGeneralError,
      generalErrorFields: [
        ...wizardGeneralErrorFields,
        fields.targeting.path,
        creativeFields.click_url.path,
        creativeFields.impression_tracking_pixel.path,
      ],
    }
  );

  const handleNormalSubmit = async () => {
    await submit();
  };

  const handleOutOfSyncSubmit = async () => {
    setDialog(
      adServerOutOfSyncPrompt({
        open: true,
        handleClose: () => {
          setDialog(null);
        },
        handleMerge: () => {
          submit();
        },
        handleOverwrite: () => {
          submit({ updateAdGroupParams: targetingOverrideParams });
        },
        outOfSyncItems: getOutOfSyncItems({ adGroup }),
      })
    );
  };

  return {
    submit: shouldShowOutOfSyncSubmitPrompt
      ? handleOutOfSyncSubmit
      : handleNormalSubmit,
    mutating: adGroupMutating || staticAdGroupMutating,
  };
};
